<template>
  <div>
    <!-- Loading bill status -->
    <div
      v-if="billStatus === null && !loadFailed"
      class="row items-center q-gutter-sm q-py-sm">
      <q-spinner
        size="20px"
        color="primary" />
      <span class="text-caption text-grey-6">{{ t('workbench.finalize.loading') }}</span>
    </div>

    <!-- Bill lookup failed -->
    <div
      v-else-if="loadFailed"
      class="row items-center q-gutter-sm q-py-sm text-grey-6">
      <q-icon
        :name="symRoundedError"
        size="20px" />
      <span class="text-caption">{{ t('workbench.finalize.loadFailed') }}</span>
      <q-btn
        flat
        dense
        size="sm"
        color="primary"
        :label="t('actions.retry')"
        @click="loadBill" />
    </div>

    <!-- Already finalized -->
    <div
      v-else-if="isAlreadyFinalized"
      class="row items-center q-gutter-sm q-py-sm">
      <q-icon
        :name="symRoundedCheckCircle"
        color="positive"
        size="22px" />
      <span class="text-body2">{{ t('workbench.finalize.alreadyFinalized') }}</span>
    </div>

    <!-- Pricing review + finalize -->
    <div
      v-else
      class="q-py-sm">
      <BillPartPricingControl
        :transaction-id="asTransactionId(billId)"
        :readonly="false"
        :show-summary="true"
        @complete="onPricingComplete"
        @error="onPricingError">
        <!-- No priceable part: apply-decisions (which finalizes server-side) is
             unavailable, so offer direct finalization instead. -->
        <template #empty-actions>
          <q-btn
            unelevated
            color="positive"
            :icon="symRoundedCheck"
            :loading="finalizing"
            :label="t('workbench.finalize.finalizeButton')"
            @click="onDirectFinalize" />
        </template>
      </BillPartPricingControl>
    </div>
  </div>
</template>

<script setup lang="ts">
import { symRoundedCheck, symRoundedCheckCircle, symRoundedError } from '@quasar/extras/material-symbols-rounded'
import { useCompositeI18n } from 'src/CrudManager/Core/composables/useCompositeI18n'
import { axiosApi } from 'src/CrudManager/Core/Utils/axios'
import { showErrorNotification } from 'src/CrudManager/Core/Utils/ToastNotification'
import BillPartPricingControl from 'src/composites/BillPartPricingControl/BillPartPricingControl.vue'
import { asTransactionId } from 'src/composites/BillPartPricingControl/types'
import { computed, onMounted, ref } from 'vue'

/**
 * In-workbench bill finalization: reviews the bill's part pricing and
 * finalizes it without leaving the AI document dialog. Applying pricing
 * decisions finalizes server-side; bills without priceable parts get a
 * direct finalize button instead.
 */
const props = defineProps<{
  /** UUID of the generated bill transaction */
  billId: string
}>()

const emit = defineEmits<{
  /** Bill is now finalized (either path) */
  finalized: []
}>()

const { t } = useCompositeI18n('core.AiDocumentProcessor')

/** Statuses the finalize endpoint refuses to re-finalize */
const FINALIZED_STATUSES = ['finalized', 'invoiced', 'sent', 'paid']

const billStatus = ref<string | null>(null)
const loadFailed = ref(false)
const finalizing = ref(false)
const finalizedEmitted = ref(false)

const isAlreadyFinalized = computed(() => billStatus.value !== null && FINALIZED_STATUSES.includes(billStatus.value))

const loadBill = async (): Promise<void> => {
  loadFailed.value = false
  billStatus.value = null
  try {
    const { data } = await axiosApi.get(`cm/transaction/${props.billId}`)
    const status = data?.data?.status ?? data?.status
    if (typeof status !== 'string') throw new Error('missing status')
    billStatus.value = status
  } catch {
    loadFailed.value = true
  }
}

onMounted(() => {
  void loadBill()
})

// BillPartPricingControl emits `complete` twice per apply — emit once only
const handleFinalized = (): void => {
  if (finalizedEmitted.value) return
  finalizedEmitted.value = true
  billStatus.value = 'finalized'
  emit('finalized')
}

const onPricingComplete = (): void => {
  handleFinalized()
}

const onPricingError = (error: string): void => {
  showErrorNotification(t('workbench.finalize.error'), error)
}

const onDirectFinalize = async (): Promise<void> => {
  finalizing.value = true
  try {
    // Returns 200 with success=false when the bill is already finalized
    const { data } = await axiosApi.get(`transaction/${props.billId}/finalize`)
    if (data?.success === false && !/finalized/i.test(String(data?.message ?? ''))) {
      throw new Error(data?.message || t('workbench.finalize.error'))
    }
    handleFinalized()
  } catch (error) {
    showErrorNotification(t('workbench.finalize.error'), error)
  } finally {
    finalizing.value = false
  }
}
</script>

<style lang="scss" scoped>
// The pricing control collapses its grids on viewport media queries, but in
// the workbench it lives in a pane far narrower than the viewport — force
// the stacked (mobile) layout regardless of window size.
:deep(.pricing-content) {
  grid-template-columns: 1fr;
  gap: 1rem;
}

:deep(.price-grid) {
  grid-template-columns: 1fr;
  gap: 0.75rem;
}

:deep(.price-arrow-block) {
  transform: rotate(90deg);
  padding: 0.25rem 0;
}

:deep(.part-chips) {
  flex-direction: row;
  flex-wrap: wrap;
}

:deep(.header-actions) {
  justify-content: flex-start;
  width: 100%;
  gap: 0.5rem;

  .q-btn {
    flex: 1;
    min-width: 0;
  }
}
</style>
